import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useStore, Panel, Badge, Meter, Empty, Modal, Field, Flash, useFlash, plural, useCan,
} from '../components/ui.jsx';
import MemberPicker from '../components/MemberPicker.jsx';
import {
  occurrenceById, bookingsFor, memberById, staffById, roomById, book, cancel,
  checkIn, undoCheckIn, entitlementFor, getState, settleClass, isSettleable,
  unresolvedFor, overrideOccurrence, cancelOccurrence, restoreOccurrence,
  isOccurrenceCancelled,
} from '../data/store.js';
import { BOOKING_STATUS } from '../lib/booking.js';
import { format } from '../lib/money.js';

export default function ClassDetail() {
  const { occurrenceId } = useParams();
  useStore();
  const can = useCan();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [flash, setFlash] = useFlash();

  const id = decodeURIComponent(occurrenceId);
  const occ = occurrenceById(id);
  const cancelled = isOccurrenceCancelled(id);

  // A cancelled date stops expanding, so occurrenceById returns nothing for it
  // — which is right for the schedule and useless for the person who wants to
  // undo the cancellation. Handle it before the not-found path.
  if (!occ && cancelled) {
    return (
      <>
        <header className="topbar"><h1>Class cancelled</h1></header>
        <div className="page">
          <div className="notice warn row">
            <span>This class was cancelled. Everyone booked was released without a fee.</span>
            <div className="spacer" />
            {can('classes.override') && (
              <button
                className="small"
                onClick={() => { restoreOccurrence(id); setFlash({ tone: 'good', message: 'Class restored. Bookings were not.' }); }}
              >
                Put it back
              </button>
            )}
          </div>
          <Empty><Link to="/schedule">Back to schedule</Link></Empty>
        </div>
      </>
    );
  }

  if (!occ) {
    return (
      <>
        <header className="topbar"><h1>Class not found</h1></header>
        <div className="page"><Empty>That class does not exist. <Link to="/schedule">Back to schedule</Link></Empty></div>
      </>
    );
  }

  const seats = bookingsFor(id);
  const roster = seats.filter((b) => [BOOKING_STATUS.BOOKED, BOOKING_STATUS.ATTENDED].includes(b.status));
  const waiting = seats
    .filter((b) => b.status === BOOKING_STATUS.WAITLISTED)
    .sort((a, b) => (a.waitlistPosition ?? 0) - (b.waitlistPosition ?? 0));
  const gone = seats.filter((b) => [
    BOOKING_STATUS.CANCELLED, BOOKING_STATUS.LATE_CANCELLED, BOOKING_STATUS.NO_SHOW,
  ].includes(b.status));

  const when = new Intl.DateTimeFormat('en-US', {
    timeZone: occ.timeZone, dateStyle: 'full', timeStyle: 'short',
  }).format(new Date(occ.startsAt));

  function handleCancel(bookingId) {
    const r = cancel(bookingId);
    if (!r.ok) return setFlash({ tone: 'bad', message: r.reason });
    const bits = [r.status === BOOKING_STATUS.LATE_CANCELLED ? 'Late cancel' : 'Cancelled'];
    if (r.feeCents) bits.push(`${format(r.feeCents)} fee applied`);
    if (r.returnsCredit) bits.push('credit returned');
    if (r.promoted) bits.push(`${memberById(r.promoted.memberId)?.name ?? 'Next member'} promoted off the waitlist`);
    return setFlash({ tone: 'good', message: `${bits.join(' · ')}.` });
  }

  const finished = isSettleable(occ);
  const unresolved = unresolvedFor(id);
  const noShowFee = getState().policy.noShowFeeCents || 0;

  function handleSettle() {
    const fee = noShowFee
      ? ` ${plural(unresolved.length, 'member', 'members')} will be charged the ${format(noShowFee)} no-show fee.`
      : '';
    if (!confirm(`Close out ${occ.name}?\n\nAnyone not checked in is recorded as a no-show.${fee}`)) return;
    const r = settleClass(id);
    if (!r.ok) return setFlash({ tone: 'bad', message: r.reason });
    return setFlash({
      tone: 'good',
      message: `Closed out — ${plural(r.attended, 'attended', 'attended')}, ${plural(r.noShows, 'no-show', 'no-shows')}${r.feeCents ? `, ${format(r.feeCents)} in fees` : ''}.`,
    });
  }

  return (
    <>
      <header className="topbar">
        <h1>{occ.name}</h1>
        <span className="sub">
          {when} · {staffById(occ.staffId)?.name ?? 'Unassigned'}
          {roomById(occ.roomId) && ` · ${roomById(occ.roomId).name}`}
        </span>
        <div className="spacer" />
        <div className="row">
          <span className="mono num">{roster.length}/{occ.capacity}</span>
          <Meter value={roster.length} max={occ.capacity} />
          {can('classes.override') && (
            <button className="small" onClick={() => setEditing(true)}>Edit this class</button>
          )}
          {can('bookings.write') && (
            <button className="primary" onClick={() => setAdding(true)}>Add member</button>
          )}
        </div>
      </header>

      <div className="page">
        <Flash flash={flash} />

        {finished && unresolved.length > 0 && (
          <div className="notice warn row">
            <span>
              This class has ended with {plural(unresolved.length, 'booking', 'bookings')} unresolved.
              {noShowFee > 0 && ` Closing out charges the ${format(noShowFee)} no-show fee to anyone who did not check in.`}
            </span>
            <div className="spacer" />
            <button className="small primary" onClick={handleSettle}>Close out class</button>
          </div>
        )}

        <Panel title={`Roster — ${roster.length}`} bodyless>
          {roster.length === 0 ? <Empty>Nobody booked yet.</Empty> : (
            <table className="data">
              <thead>
                <tr><th>Member</th><th>Status</th><th>Booked</th><th className="right">Actions</th></tr>
              </thead>
              <tbody>
                {roster.map((b) => {
                  const member = memberById(b.memberId);
                  return (
                    <tr key={b.id}>
                      <td>
                        {member ? <Link to={`/members/${member.id}`}>{member.name}</Link> : <span className="mono">{b.memberId}</span>}
                        {b.consumedCredit && <> <Badge tone="neutral">credit</Badge></>}
                        {b.promotedAt && <> <Badge tone="accent">promoted</Badge></>}
                      </td>
                      <td><Badge status={b.status} /></td>
                      <td className="mono" style={{ color: 'var(--ink-3)' }}>
                        {b.bookedAt
                          ? new Intl.DateTimeFormat('en-US', {
                            timeZone: occ.timeZone, dateStyle: 'medium',
                          }).format(new Date(b.bookedAt))
                          : '—'}
                      </td>
                      <td className="right">
                        <div className="row" style={{ justifyContent: 'flex-end' }}>
                          {b.status === BOOKING_STATUS.ATTENDED
                            ? <button className="small" onClick={() => undoCheckIn(b.id)}>Undo check-in</button>
                            : <button className="small" onClick={() => checkIn(b.id)}>Check in</button>}
                          <button className="small danger" onClick={() => handleCancel(b.id)}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Panel>

        {waiting.length > 0 && (
          <Panel title={`Waitlist — ${waiting.length}`} bodyless>
            <table className="data">
              <thead><tr><th style={{ width: 60 }}>#</th><th>Member</th><th className="right">Actions</th></tr></thead>
              <tbody>
                {waiting.map((b) => (
                  <tr key={b.id}>
                    <td className="mono num">{b.waitlistPosition}</td>
                    <td><Link to={`/members/${b.memberId}`}>{memberById(b.memberId)?.name ?? b.memberId}</Link></td>
                    <td className="right">
                      <button className="small danger" onClick={() => handleCancel(b.id)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}

        {gone.length > 0 && (
          <Panel title="Cancelled and no-shows" bodyless>
            <table className="data">
              <thead><tr><th>Member</th><th>Status</th><th className="right">Fee</th></tr></thead>
              <tbody>
                {gone.map((b) => (
                  <tr key={b.id}>
                    <td>{memberById(b.memberId)?.name ?? b.memberId}</td>
                    <td><Badge status={b.status} /></td>
                    <td className="right mono num">{b.feeCents ? format(b.feeCents) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}
      </div>

      {editing && (
        <EditOccurrence
          occurrence={occ}
          onClose={() => setEditing(false)}
          onFlash={(f) => { setEditing(false); setFlash(f); }}
        />
      )}

      {adding && (
        <AddToClass
          occurrence={occ}
          onClose={() => setAdding(false)}
          onResult={(r) => {
            setAdding(false);
            if (!r.ok) return setFlash({ tone: 'bad', message: r.reason });
            return setFlash({
              tone: 'good',
              message: r.action === 'waitlist'
                ? `Added to the waitlist at position ${r.position}.`
                : 'Booked into the class.',
            });
          }}
        />
      )}
    </>
  );
}

function AddToClass({ occurrence, onClose, onResult }) {
  const [memberId, setMemberId] = useState('');
  const booked = new Set(bookingsFor(occurrence.id)
    .filter((b) => [BOOKING_STATUS.BOOKED, BOOKING_STATUS.WAITLISTED, BOOKING_STATUS.ATTENDED].includes(b.status))
    .map((b) => b.memberId));

  const preview = memberId ? entitlementFor(memberId, occurrence, occurrence.date) : null;
  const full = occurrence.booked >= occurrence.capacity;

  return (
    <Modal
      title={`Add to ${occurrence.name}`}
      onClose={onClose}
      footer={(
        <button
          className="primary"
          // Blocked is blocked: offering the button and then failing with the
          // same reason already shown above it is a pointless round trip.
          disabled={!memberId || (preview && !preview.result.ok)}
          onClick={() => onResult(book(memberId, occurrence.id))}
        >
          {full ? 'Add to waitlist' : 'Book'}
        </button>
      )}
    >
      <div className="stack">
        <MemberPicker
          value={memberId}
          onChange={setMemberId}
          exclude={booked}
          placeholder="Search for a member to add…"
          // Eligibility per row, so the answer is visible before anyone is
          // picked rather than after.
          detail={(m) => {
            const check = entitlementFor(m.id, occurrence, occurrence.date);
            return check.result.ok
              ? <Badge tone="good">clear</Badge>
              : <Badge tone="bad">blocked</Badge>;
          }}
        />

        {preview && (
          preview.result.ok
            ? (
              <div className="notice good">
                Eligible on <b>{preview.plan?.name}</b>
                {preview.membership?.creditsRemaining > 0 && ` · ${preview.membership.creditsRemaining} credits left`}
              </div>
            )
            : <div className="notice bad">{preview.result.reason}</div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Change or cancel ONE date of a recurring class.
 *
 * Everything here writes an override on the template rather than editing a
 * stored row, because occurrences are computed. That is what makes "the Tuesday
 * 6am has a different coach this week" cheap and "move the whole series" a
 * separate, deliberate act.
 */
function EditOccurrence({ occurrence: occ, onClose, onFlash }) {
  const state = getState();
  const [staffId, setStaffId] = useState(occ.staffId ?? '');
  const [roomId, setRoomId] = useState(occ.roomId ?? '');
  const [capacity, setCapacity] = useState(String(occ.capacity));
  const [error, setError] = useState(null);

  const seated = bookingsFor(occ.id).filter(
    (b) => [BOOKING_STATUS.BOOKED, BOOKING_STATUS.ATTENDED].includes(b.status),
  ).length;
  const waiting = bookingsFor(occ.id).filter(
    (b) => b.status === BOOKING_STATUS.WAITLISTED,
  ).length;

  const when = new Intl.DateTimeFormat('en-US', {
    timeZone: occ.timeZone, dateStyle: 'full',
  }).format(new Date(occ.startsAt));

  function save() {
    const r = overrideOccurrence(occ.id, {
      staffId: staffId || null,
      roomId: roomId || null,
      capacity: Number(capacity) || null,
    });
    if (!r.ok) return setError(r.reason);
    return onFlash({ tone: 'good', message: `${occ.name} updated for ${occ.date} only.` });
  }

  function cancelClass() {
    const who = seated + waiting;
    const warning = who > 0
      ? `\n\n${plural(who, 'person', 'people')} will be released. Nobody is charged a fee and any credits are returned.`
      : '';
    if (!confirm(`Cancel ${occ.name} on ${occ.date}?${warning}`)) return;
    const r = cancelOccurrence(occ.id);
    if (!r.ok) return onFlash({ tone: 'bad', message: r.reason });
    return onFlash({
      tone: 'good',
      message: r.released
        ? `Class cancelled — ${plural(r.released, 'booking', 'bookings')} released${r.creditsReturned ? `, ${plural(r.creditsReturned, 'credit', 'credits')} returned` : ''}.`
        : 'Class cancelled.',
    });
  }

  return (
    <Modal
      title={`Edit ${occ.name}`}
      onClose={onClose}
      footer={<button className="primary" onClick={save}>Save this date</button>}
    >
      <div className="stack">
        <p className="hint" style={{ margin: 0 }}>
          {when} — changes here apply to this date only. The rest of the series
          is untouched.
        </p>

        {error && <div className="notice bad">{error}</div>}

        <div className="form-grid">
          <Field label="Coach">
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)}>
              <option value="">Unassigned</option>
              {state.staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Room">
            <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
              <option value="">Unassigned</option>
              {(state.rooms || []).map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Capacity">
            <input
              type="number"
              min={seated || 1}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </Field>
        </div>

        {seated > 0 && (
          <p className="hint" style={{ margin: 0 }}>
            {plural(seated, 'person', 'people')} already booked, so capacity cannot go below {seated}.
          </p>
        )}

        <div className="notice warn row">
          <span>Cancelling releases everyone without a fee and returns their credits.</span>
          <div className="spacer" />
          <button className="small danger" onClick={cancelClass}>Cancel this class</button>
        </div>
      </div>
    </Modal>
  );
}
